import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, BarChart3, UserPlus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDesktopExpanded } from '../../hooks/useDesktopExpanded';
import { Button } from '../common';
import { compactInputBase } from './controls';
import { listAllStudents, listClasses, listDepartments, isPredeployError } from './academyApi';
import { orderClasses, stateFilterOf, STATE_FILTERS } from './academyModel';
import { todayKST } from './periodDates';
import { ErrorNotice, Spinner, StateBadge, ToneBadge, useAcademyVersion } from './ui';
import StudentIdBadge from './StudentIdBadge';
import FloatingScrollArea from '../common/FloatingScrollArea';
import CreateStudentModal from './modals/CreateStudentModal';
import '../../styles/desktop-expanded.css';

const EMPTY_FILTERS = { q: '', state: 'all', classId: '', teacherStaffId: '', studentKind: 'all' };
/**
 * 강사는 「재원」부터(오너 결정 2026-10-13, 백엔드 8단계 전달 §4-H) — 강사 범위는 v1 담당 학생이 대부분이고 그 대부분이 「등록 전」이라
 * 「전체」로 열면 재원생이 묻힌다. 다른 상태는 거르기를 바꿔 본다
 */
const TEACHER_FILTERS = { ...EMPTY_FILTERS, state: 'active' };
/** 「재원」만 고른 조회(검색어 · 반 · 담임 · 부서 없음)인가 */
const isActiveView = (a) => a.state === 'active' && !a.q && !a.classId && !a.teacherStaffId && a.departmentId == null && a.studentKind === 'all';

/**
 * 학생 v2 왼쪽 학생 표 — 학습 v2 의 StudentTablePanel.jsx 좁은 모드(compact)를 복사해 칸만 바꿨다(설계 §2 · §9-2).
 * 원본은 손대지 않는다(이원화 — 두 메뉴를 합칠 때 한쪽을 지우기만 하면 되게).
 *
 * 그대로 옮긴 것: 칸 모양(rounded-3xl · 그림자) · 머리 여백 12px · 제목 18px · 2열 검색 격자 + 세로 단추 ·
 *   둥근 단추 줄 · 행 높이 · 고른 행 초록 15% · 쪽 넘김 · 한 쪽 행 수 계산.
 * 바꾼 것: 목록 API(학생 v2 S1) · 칸 내용(학생/이메일 · 학교/학년 · 반/담임 · 상태) · 단추 줄(학교급 → 부서) ·
 *   검색 칸(학생·학교 검색 · 상태 · 반 · 담임) · 머리 오른쪽 단추([새 학생] 매니저 · 휴대폰 [현황]).
 * 이름 아래는 학습 v2 처럼 이메일(오너 2026-10-10). 부서는 단추 줄과 상세에서 본다.
 * 첫 칼럼은 SID(student_id). 출결번호(student_no)는 학생 상세·검색에서 별도 항목으로 쓴다.
 * 로그인이 잠긴 학생은 학습 v2 처럼 원이 빨갛다.
 *
 * 목록은 늘 오늘 기준 — 기준일 고르기는 통계 쪽에만 둔다(설계 §2).
 * 강사 계정은 서버가 범위 학생만 준다(scope: mine). 반 칸은 본인 반을 먼저 보인다.
 */
function StudentRosterPanel({ selectedStudentId = null, onSelectStudent, isTeacher = false, myStaffId = null, onOpenStats }) {
  const version = useAcademyVersion();
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const { isMobileView } = useDesktopExpanded();
  const tight = isMobileView;
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const initialFilters = isTeacher ? TEACHER_FILTERS : EMPTY_FILTERS;
  const [filters, setFilters] = useState(initialFilters);
  const [departmentId, setDepartmentId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [departments, setDepartments] = useState([]);
  // '조회'로 제출된 조건 — 쪽 넘김·창 크기 재요청은 입력칸의 라이브 값이 아니라 이 값을 쓴다(원본과 같은 방식)
  const appliedRef = useRef({ ...initialFilters, departmentId: null });
  const requestSeqRef = useRef(0);
  const controllerRef = useRef(null);
  const listCacheRef = useRef(null);
  const resizeFetchRef = useRef(null);

  // 화면 크기에 맞춘 한 쪽 행 수 — StudentTablePanel.jsx:73-100 과 같은 식
  const calculateItemsPerPage = () => {
    try {
      const windowHeight = window.innerHeight;
      const buffer = 10;
      let availableHeight;
      if (isMobileView) {
        const cardHeight = windowHeight - 50 - 68 - 29;
        availableHeight = cardHeight - (154 + 12 + 35 + 40 + buffer);
      } else {
        const cardHeight = windowHeight - 96;
        availableHeight = cardHeight - (32 + 28 + 80 + 16 + 40 + 60 + buffer);
      }
      return Math.max(5, Math.min(Math.floor(availableHeight / 48), isMobileView ? 30 : 50));
    } catch (error) {
      return 10;
    }
  };

  const fetchStudents = async (pageNum = 1) => {
    const seq = ++requestSeqRef.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const a = appliedRef.current;
    const cacheKey = JSON.stringify([version, todayKST(), { ...a, studentKind: undefined }]);
    const perPage = calculateItemsPerPage();
    setIsLoading(true);
    setListError(null);
    setStudents([]);
    try {
      let rows = listCacheRef.current?.key === cacheKey ? listCacheRef.current.rows : null;
      if (!rows) {
        // 서버는 SID 오름차순만 지원한다. 전체 검색 결과를 정렬한 뒤 화면의 쪽을 나눈다.
        const result = await listAllStudents({
          state: stateFilterOf(a.state).states,
          q: a.q,
          classId: a.classId || null,
          teacherStaffId: a.teacherStaffId || null,
          departmentId: a.departmentId,
          sort: 'student_id',
          signal: controller.signal,
        }, getAccessToken);
        if (seq !== requestSeqRef.current) return;
        rows = [...result.items].sort((x, y) => y.student_id - x.student_id);
        listCacheRef.current = { key: cacheKey, rows };
      }
      if (seq !== requestSeqRef.current) return;
      // 서버에서 받은 전체 검색 결과를 분류한 뒤 총인원과 페이지를 계산한다.
      if (!isTeacher && a.studentKind !== 'all') rows = rows.filter((student) => student.is_test === (a.studentKind === 'test'));
      const pages = Math.max(1, Math.ceil(rows.length / perPage));
      const nextPage = Math.min(pageNum, pages);
      setStudents(rows.slice((nextPage - 1) * perPage, nextPage * perPage));
      setTotalPages(pages);
      setTotalItems(rows.length);
      setPage(nextPage);
      setPageInputValue(String(nextPage));
    } catch (error) {
      if (seq !== requestSeqRef.current || error?.name === 'AbortError') return;
      if (error.httpStatus === 401) { navigate('/signin'); return; }
      setStudents([]);
      setTotalPages(1);
      setTotalItems(0);
      setPage(1);
      setPageInputValue('1');
      setListError(error);
    } finally {
      if (seq === requestSeqRef.current) setIsLoading(false);
    }
  };

  const search = (next = filters, dept = departmentId) => {
    listCacheRef.current = null;
    appliedRef.current = { ...next, departmentId: dept };
    fetchStudents(1);
  };
  const handleReset = () => {
    setFilters(initialFilters);
    setDepartmentId(null);
    search(initialFilters, null);
  };
  // 상태 칸은 바꾸면 바로 조회한다 — 부서 단추와 같이(백엔드 4단계 전달 §4-G). 검색어 · 반 · 담임은 [조회]로
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const next = { ...filters, [name]: value };
    setFilters(next);
    if (name === 'state') search(next);
    if (name === 'studentKind') {
      appliedRef.current = { ...appliedRef.current, studentKind: value };
      fetchStudents(1);
    }
  };
  const handleKeyPress = (e) => { if (e.key === 'Enter') search(); };
  const toggleDepartment = (id) => {
    const next = departmentId === id ? null : id;
    setDepartmentId(next);
    search(filters, next);
  };
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || isLoading || listError) return;
    fetchStudents(newPage);
  };

  // 첫 목록 + 거르기 재료(반·부서). 강사는 본인 직원 번호가 서면 본인 반만 고르기에 올린다
  useEffect(() => {
    fetchStudents(1);
    return () => { requestSeqRef.current += 1; controllerRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 거르기 재료도 저장 소식(version)에 다시 받는다 — 반을 만들거나 담임을 바꾸고 비활성화하면 반 · 담임 고르기가 바로 맞게(백엔드 3단계)
  useEffect(() => {
    let alive = true;
    if (isTeacher && myStaffId == null) return undefined;
    listClasses({ state: 'open', teacherStaffId: isTeacher ? myStaffId : null }, getAccessToken)
      .then((rows) => { if (alive) setClasses(rows); })
      .catch(() => { if (alive) setClasses([]); });
    return () => { alive = false; };
  }, [isTeacher, myStaffId, getAccessToken, version]);
  useEffect(() => {
    let alive = true;
    listDepartments({}, getAccessToken)
      .then((rows) => { if (alive) setDepartments(rows); })
      .catch(() => { if (alive) setDepartments([]); });
    return () => { alive = false; };
  }, [getAccessToken, version]);

  // 다른 곳에서 저장하면(등록·퇴원·반 이동 …) 지금 쪽을 다시 부른다
  const versionRef = useRef(version);
  useEffect(() => {
    if (versionRef.current === version) return;
    versionRef.current = version;
    fetchStudents(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  // 창 크기 재요청용 최신 클로저 — 원본(StudentTablePanel.jsx:276-304)과 같은 방식
  resizeFetchRef.current = () => { if (students.length > 0) fetchStudents(page); };
  useEffect(() => {
    let timer = null;
    const handleResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { if (resizeFetchRef.current) resizeFetchRef.current(); }, 200);
    };
    window.addEventListener('resize', handleResize);
    return () => { if (timer) clearTimeout(timer); window.removeEventListener('resize', handleResize); };
  }, []);

  // 담임 고르기 재료 — 운영 반의 지금 담임(중복 없이). 별도 직원 목록을 부르지 않는다
  const teacherOptions = [];
  classes.forEach((c) => {
    if (c.teacher && !teacherOptions.some((t) => t.staff_id === c.teacher.staff_id)) teacherOptions.push(c.teacher);
  });
  teacherOptions.sort((a, b) => a.name.localeCompare(b.name, 'ko'));

  const renderPageNumbers = () => {
    const nums = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    const end = Math.min(start + maxVisible - 1, totalPages);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    const numBtnClass = tight
      ? 'w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 text-xs'
      : 'page-button';
    const numBtnStyle = tight ? {} : { borderRadius: '1.5rem' };
    const dotsClass = tight ? 'px-1 text-gray-400' : 'page-dots';
    const input = (i) => {
      const w = tight ? Math.max(2, String(pageInputValue).length * 0.6 + 1.2) : Math.max(2.5, String(pageInputValue).length * 0.6 + 1.5);
      return (
        <input
          key={i}
          type="text"
          value={pageInputValue}
          onChange={(e) => { if (e.target.value === '' || /^\d+$/.test(e.target.value)) setPageInputValue(e.target.value); }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            const n = parseInt(pageInputValue, 10);
            if (n >= 1 && n <= totalPages) { e.target.blur(); setTimeout(() => handlePageChange(n), 100); } else setPageInputValue(String(page));
          }}
          onMouseUp={(e) => e.preventDefault()}
          onFocus={(e) => e.target.select()}
          onBlur={() => setPageInputValue(String(page))}
          className={tight ? 'flex items-center justify-center rounded-full border border-primary bg-primary text-xs font-bold text-white' : 'page-button active font-bold'}
          style={tight
            ? { width: `${w}rem`, minWidth: '2rem', height: '2rem', textAlign: 'center', outline: 'none', cursor: 'text', padding: '0 4px' }
            : { borderRadius: '1.5rem', width: `${w}rem`, minWidth: '2.5rem', height: '40px', textAlign: 'center', outline: 'none', border: 'none', cursor: 'text', padding: '0 8px' }}
          aria-label="페이지 번호 입력"
        />
      );
    };
    if (start > 1) {
      nums.push(<button key={1} type="button" className={numBtnClass} style={numBtnStyle} onClick={() => handlePageChange(1)}>1</button>);
      if (start > 2) nums.push(<span key="e1" className={dotsClass}>...</span>);
    }
    for (let i = start; i <= end; i += 1) {
      nums.push(page === i ? input(i) : (
        <button key={i} type="button" className={numBtnClass} style={numBtnStyle} onClick={() => handlePageChange(i)} aria-label={`${i} 페이지`}>{i}</button>
      ));
    }
    if (end < totalPages) {
      if (end < totalPages - 1) nums.push(<span key="e2" className={dotsClass}>...</span>);
      nums.push(<button key={totalPages} type="button" className={numBtnClass} style={numBtnStyle} onClick={() => handlePageChange(totalPages)}>{totalPages}</button>);
    }
    return nums;
  };

  const inputCls = `w-full ${compactInputBase}`;
  const thCls = `${tight ? 'px-2' : 'px-3'} py-2 text-xs font-medium text-gray-500 whitespace-nowrap`;
  const showRows = !isLoading && !listError;

  return (
    <div className="flex size-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      {/* 머리 */}
      <div className="shrink-0 border-b" style={{ padding: '12px' }}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <h3 className={`shrink-0 whitespace-nowrap font-semibold ${tight ? 'text-sm' : 'text-lg'}`}>학생 목록</h3>
            <p className="truncate text-xs text-gray-500">
              - 총 <span className="font-semibold text-primary">{totalItems || 0}</span>명 중{' '}
              <span className="font-semibold text-primary">{showRows ? students.length : 0}</span>명 표시 중
            </p>
          </div>
          {/* 새 학생(요청서 S3) — 매니저만. 학생 정보만 만들거나 수강 등록까지 한다 — 「등록」은 재원 시작이라 단추 이름에 쓰지 않는다
              (오너 결정 2026-10-13, 백엔드 2b 전달 §3-D). 얼굴은 학습 v2 의 [테스트] 단추와 같다 */}
          {!isTeacher && (
            <Button
              variant="primary-outline"
              size="xs"
              onClick={() => setCreating(true)}
              title="새 학생"
              className="ml-auto min-h-8"
            >
              <UserPlus size={14} className="mr-1" />
              새 학생
            </Button>
          )}
          {/* 휴대폰은 칸이 하나라 통계로 가는 단추를 목록 머리에 둔다(설계 §7) */}
          {onOpenStats && (
            <Button
              variant="primary-outline"
              size="xs"
              onClick={onOpenStats}
              title={isTeacher ? '내 반 현황' : '학원 현황'}
              className="min-h-8"
            >
              <BarChart3 size={14} className="mr-1" />
              현황
            </Button>
          )}
        </div>
        <div className="mt-3 flex gap-2">
          <div className="grid flex-1 grid-cols-2 gap-2">
            {/* 서버 q — 이름 · 지금 학교 이름 부분 일치 + 출결번호 4자리 정확히 일치(백엔드 2단계 전달 §1 · §4-C) */}
            <input type="text" name="q" placeholder="이름·학교·출결번호" value={filters.q} onChange={handleFilterChange} onKeyDown={handleKeyPress} className={inputCls} aria-label="이름·학교·출결번호 검색" />
            <select name="state" value={filters.state} onChange={handleFilterChange} className={`${inputCls} bg-white`} aria-label="상태">
              {STATE_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <select name="classId" value={filters.classId} onChange={handleFilterChange} className={`${inputCls} bg-white`} aria-label={isTeacher ? '내 반' : '반'}>
              <option value="">{isTeacher ? '내 반 전체' : '반'}</option>
              {classes.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
            </select>
            {isTeacher ? (
              <input type="text" value={classes[0]?.teacher?.name || '담임'} disabled className={inputCls} aria-label="담임" />
            ) : (
              <select name="teacherStaffId" value={filters.teacherStaffId} onChange={handleFilterChange} className={`${inputCls} bg-white`} aria-label="담임">
                <option value="">담임</option>
                {teacherOptions.map((t) => <option key={t.staff_id} value={t.staff_id}>{t.name}</option>)}
              </select>
            )}
          </div>
          <div className="flex w-16 flex-col gap-2">
            <Button onClick={handleReset} variant="secondary" size="sm" rounded="3xl" className="min-h-[22px] flex-1">초기화</Button>
            <Button onClick={() => search()} variant="primary" size="sm" rounded="3xl" className="min-h-[22px] flex-1">조회</Button>
          </div>
        </div>
        {/* 부서 단추 줄 — 학습 v2 의 학교급 단추 줄(StudentTablePanel.jsx:455-474)과 같은 얼굴 */}
        {(departments.length > 0 || !isTeacher) && (
          <div className="mt-3 flex min-w-0 items-center gap-2">
            <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto">
              {departments.map((d) => (
                <button
                  key={d.department_id}
                  type="button"
                  aria-pressed={departmentId === d.department_id}
                  onClick={() => toggleDepartment(d.department_id)}
                  className={`flex-1 whitespace-nowrap px-4 py-1.5 text-sm transition-all duration-200 ${departmentId === d.department_id ? 'bg-primary font-bold text-white' : 'bg-gray-100 font-medium text-gray-700'}`}
                  style={{ borderRadius: '20px' }}
                >
                  {d.name}
                </button>
              ))}
            </div>
            {!isTeacher && <select name="studentKind" value={filters.studentKind} onChange={handleFilterChange} aria-label="학생 구분"
              className={`w-28 shrink-0 ${compactInputBase} bg-white`}>
              <option value="all">전체 학생</option>
              <option value="regular">일반 학생</option>
              <option value="test">테스트 학생</option>
            </select>}
          </div>
        )}
      </div>

      {/* 목록 */}
      <div className="flex-1 overflow-hidden" style={{ marginTop: '8px', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {listError && (isPredeployError(listError)
          ? <ErrorNotice error={listError} />
          : <ErrorNotice error={listError} onRetry={() => fetchStudents(1)} retryLabel="다시 조회" />)}
        <FloatingScrollArea aria-label="학생 목록 스크롤" className="flex-1" style={{ width: '100%', display: listError && isPredeployError(listError) ? 'none' : undefined }}>
          <table aria-busy={isLoading} className="w-full min-w-[440px] table-fixed">
            <colgroup>
              <col style={{ width: tight ? '40px' : '48px' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: tight ? '96px' : '112px' }} />
              <col />
              <col style={{ width: tight ? '68px' : '76px' }} />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                <th className={`${thCls} text-center`}>SID</th>
                <th className={`${thCls} text-left`}>학생/이메일</th>
                <th className={`${thCls} text-left`}>학교/학년</th>
                <th className={`${thCls} text-left`}>반/담임</th>
                <th className={`${thCls} text-right`}>상태</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoading && (
                <tr><td colSpan={5}><Spinner label="학생 목록 조회 중" /></td></tr>
              )}
              {showRows && students.map((s) => {
                const isSelected = selectedStudentId === s.student_id;
                const orderedClasses = orderClasses(s.classes, isTeacher ? myStaffId : null);
                const classTitle = orderedClasses.map((c) => `${c.class_name}(${c.teacher?.name || '담임 없음'})`).join(' · ') || '반 없음';
                return (
                  <tr
                    key={s.student_id}
                    onClick={() => onSelectStudent && onSelectStudent(s.student_id)}
                    aria-selected={isSelected}
                    className={`cursor-pointer border-b border-gray-100 transition-colors duration-150 ${isSelected ? 'bg-primary/15' : 'hover:bg-primary/10'}`}
                  >
                    <td className={`${tight ? 'rounded-l-xl px-1' : 'rounded-l-3xl px-2'} py-2 text-center`}>
                      <StudentIdBadge id={s.student_id} blocked={s.is_blocked} />
                    </td>
                    <td className={`${tight ? 'max-w-[90px] px-1' : 'px-2'} whitespace-nowrap py-2 transition-colors duration-150`}>
                        <div className="min-w-0 overflow-hidden">
                          <div className="flex min-w-0 items-center gap-1 text-xs font-medium leading-tight text-gray-900"><span className="truncate">{s.student_name}</span>{s.is_test && <ToneBadge>테스트</ToneBadge>}</div>
                          <div className="truncate text-xs leading-tight text-gray-500" title={s.student_email}>{s.student_email || '-'}</div>
                        </div>
                    </td>
                    {/* 학교/학년 — 학교 갱신 필요면 노란 점 */}
                    <td className={`${tight ? 'px-1' : 'px-2'} py-2 transition-colors duration-150`}>
                      <div className={`break-words text-xs leading-tight ${s.school ? 'text-gray-900' : 'text-gray-400'}`}>{s.school?.name || '학교 미지정'}</div>
                      <div className="flex items-center gap-1 whitespace-nowrap text-xs leading-tight text-gray-500">
                        {s.grade_label || '-'}
                        {s.needs_school_update && <span title="학교 갱신 필요" aria-label="학교 갱신 필요" className="inline-block size-1.5 rounded-full bg-warning" />}
                      </div>
                    </td>
                    {/* 반마다 한 줄: 반 배지 · 그 반의 담임. */}
                    <td title={classTitle} className={`${tight ? 'px-1' : 'px-2'} py-2 transition-colors duration-150`}>
                      {orderedClasses.length > 0 ? (
                        <div className="space-y-1">
                          {orderedClasses.map((c) => (
                            <div key={c.class_id} className="flex min-w-0 items-center gap-1">
                              <span
                                title={`${c.class_name} · ${c.teacher?.name || '담임 없음'}`}
                                className="min-w-0 whitespace-normal break-words rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium leading-tight text-gray-700"
                              >
                                {c.class_name}
                              </span>
                              <span aria-hidden="true" className="shrink-0 text-[11px] text-gray-400">·</span>
                              <span className="max-w-[50%] shrink-0 break-words text-[11px] leading-tight text-gray-500">{c.teacher?.name || '담임 없음'}</span>
                            </div>
                          ))}
                        </div>
                      ) : <div className="text-xs leading-tight text-gray-400">반 없음</div>}
                    </td>
                    {/* 상태 */}
                    <td className={`${tight ? 'rounded-r-xl px-2' : 'rounded-r-3xl px-3'} whitespace-nowrap py-2 text-right align-middle`}>
                      <StateBadge row={s} twoLine />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </FloatingScrollArea>

        {/* 쪽 넘김 */}
        {showRows && students.length > 0 && (
          <div className="shrink-0 bg-white pb-4">
            <div className={tight ? 'flex items-center justify-center gap-2' : 'desktop-pagination'}>
              <button
                type="button"
                className={tight ? 'flex size-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50' : 'page-button'}
                style={!tight ? { borderRadius: '1.5rem' } : {}}
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                aria-label="이전 페이지"
              >
                <ChevronLeft size={16} aria-hidden="true" />
              </button>
              <div className="flex gap-1">{renderPageNumbers()}</div>
              <button
                type="button"
                className={tight ? 'flex size-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50' : 'page-button'}
                style={!tight ? { borderRadius: '1.5rem' } : {}}
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
                aria-label="다음 페이지"
              >
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {/* 「재원」에 아무도 없으면 — 일괄 이관이 없어 v1 학생은 「등록 전」에서 찾아 [등록]한다(백엔드 4단계 전달 §4-A).
            강사는 등록할 수 없다(403) — 등록은 매니저가 한다고만 말한다(백엔드 8단계 전달 §4-H) */}
        {showRows && students.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            {isActiveView(appliedRef.current)
              ? <>재원생이 없습니다.<p className="mt-1 text-xs text-gray-400">{isTeacher ? '등록은 매니저가 합니다.' : 'v1 학생은 상태 「등록 전」에서 찾아 [등록]하세요.'}</p></>
              : '조건에 맞는 학생이 없습니다.'}
          </div>
        )}
      </div>
      {creating && <CreateStudentModal onClose={() => setCreating(false)} onCreated={(id) => onSelectStudent && onSelectStudent(id)} />}
    </div>
  );
}

export default StudentRosterPanel;
